import { z } from 'zod';
import { noteAuthorSchema } from './note.js';

/**
 * Matrix folders (issue #19, `docs/specs/matrices.md` « Dossiers de
 * matrices »): per company, a single level, a matrix in at most one folder.
 */

// ── Limits and error codes ───────────────────────────────────────────────────

export const FOLDER_NAME_MAX = 80;
export const FOLDER_DESCRIPTION_MAX = 2000;

/** 409 on create and rename: another folder of the company has this name once normalised. */
export const FOLDER_NAME_TAKEN = 'FOLDER_NAME_TAKEN';
/** 422 on `PUT /matrices/:id/folder` and `POST /matrices`: the folder belongs to another company. */
export const FOLDER_COMPANY_MISMATCH = 'FOLDER_COMPANY_MISMATCH';

/**
 * A folder name reduced to what makes two names « the same »: lower case, no
 * accents, inner whitespace collapsed, ends trimmed. The server stores it as
 * `name_key` (unique per company) and the client uses it to sort and to warn
 * before the 409.
 */
export function normalizeFolderName(name: string): string {
  return name.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// ── Responses ────────────────────────────────────────────────────────────────

/**
 * A folder with its counters. `matrixCount` is every matrix in it;
 * `openNoteCount` is the open discussions of the folder **the caller may see**.
 * `descriptionUpdatedBy` / `descriptionUpdatedAt` are null while it was never
 * written.
 */
export const matrixFolderSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  descriptionUpdatedBy: noteAuthorSchema.nullable(),
  descriptionUpdatedAt: z.string().datetime().nullable(),
  matrixCount: z.number().int().min(0),
  openNoteCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MatrixFolder = z.infer<typeof matrixFolderSchema>;

// ── Inputs ───────────────────────────────────────────────────────────────────

const folderNameSchema = z.string().trim().min(1).max(FOLDER_NAME_MAX);
const folderDescriptionSchema = z.string().trim().max(FOLDER_DESCRIPTION_MAX);

/** `POST /companies/:id/folders`. */
export const createFolderInputSchema = z.object({
  name: folderNameSchema,
  description: folderDescriptionSchema.optional(),
});
export type CreateFolderInput = z.infer<typeof createFolderInputSchema>;

/** `PATCH /matrix-folders/:id` — an empty description clears it. */
export const updateFolderInputSchema = z
  .object({
    name: folderNameSchema.optional(),
    description: folderDescriptionSchema.optional(),
  })
  .refine((patch) => patch.name !== undefined || patch.description !== undefined, {
    message: 'At least one of name or description must be present.',
  });
export type UpdateFolderInput = z.infer<typeof updateFolderInputSchema>;

/** `PUT /matrices/:id/folder` — `null` puts the matrix back « Hors dossier ». */
export const moveMatrixToFolderInputSchema = z.object({
  folderId: z.string().uuid().nullable(),
});
export type MoveMatrixToFolderInput = z.infer<typeof moveMatrixToFolderInputSchema>;

/** The answer to `PUT /matrices/:id/folder`: the matrix and the folder it now sits in. */
export const moveMatrixToFolderResponseSchema = z.object({
  id: z.string().uuid(),
  folderId: z.string().uuid().nullable(),
});
export type MoveMatrixToFolderResponse = z.infer<typeof moveMatrixToFolderResponseSchema>;
